import useScreenType from '../../../hooks/utility/useScreenType';
import useRouter from '../../../hooks/navigation/useRouter';
import PaymentCoordinator from '../../../components-library/payment/PaymentCoordinator';
import React, { ComponentPropsWithoutRef, PropsWithChildren, useEffect, useRef } from 'react';
import GiftCardNameRow from '../../../features/gift-cards/components/GiftCardNameRow';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import classnames from 'classnames';
import MobileGiftCardsCheckoutScreen from './MobileGiftCardsCheckoutScreen';
import DesktopGiftCardsCheckoutScreen from './DesktopGiftCardsCheckoutScreen';
import { useTranslation } from 'react-i18next';
import { SCREENS } from '../../../services/exports/Constants';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createBuyGiftCardSchema } from '../../../modals/buy-gift-card/types';
import giftCardsBasketSlice from '../../../store/slices/GiftCardBasketSlice';
import { useGiftCardsBasket } from '../../../hooks/basket/useGiftCardBasket';
import useGiftCardLimits from '../../../features/gift-cards/hooks/useGiftCardLimits';

const WithWrapper = ({ children }: PropsWithChildren) => {
  return <PaymentCoordinator>{children}</PaymentCoordinator>;
};

const GiftCardsCheckoutScreen = () => {
  const { isDesktop } = useScreenType();
  const { navigate } = useRouter();
  const { item: giftCardsBasket } = useGiftCardsBasket();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const searchParamsChecked = useRef(false);
  const { min, max } = useGiftCardLimits();

  useEffect(() => {
    if (searchParamsChecked.current) {
      return;
    }

    const dataFromQuery =
      searchParams.size &&
      createBuyGiftCardSchema(min / 100, max / 100).safeParse({
        value: +searchParams.get('value'),
        quantity: +searchParams.get('quantity'),
        email: searchParams.get('email'),
      });

    if (dataFromQuery && dataFromQuery.success) {
      dispatch(giftCardsBasketSlice.actions.addToBasket(dataFromQuery.data));
    }

    searchParams.size && setSearchParams({}, { replace: true });
    searchParamsChecked.current = true;
  }, []);

  useEffect(() => {
    if (searchParams.size) {
      return;
    }

    if (!searchParamsChecked.current) {
      return;
    }

    if (giftCardsBasket) {
      return;
    }

    navigate({
      web: {
        to: SCREENS.HOME_SCREEN,
      },
    });
  }, [giftCardsBasket, searchParams]);

  if (!giftCardsBasket) {
    return null;
  }

  return (
    <WithWrapper>{isDesktop ? <DesktopGiftCardsCheckoutScreen /> : <MobileGiftCardsCheckoutScreen />}</WithWrapper>
  );
};

export default GiftCardsCheckoutScreen;

GiftCardsCheckoutScreen.GiftCardRow = () => {
  const { item, total } = useGiftCardsBasket();

  return (
    <GiftCardsCheckoutScreen.Row amount={total}>
      <GiftCardNameRow>
        <span>x {item.quantity}</span>
      </GiftCardNameRow>
    </GiftCardsCheckoutScreen.Row>
  );
};

GiftCardsCheckoutScreen.DeliveryRow = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:GiftCardsCheckoutScreen.summary' });

  return <GiftCardsCheckoutScreen.Row amount={0}>{t('delivery')}</GiftCardsCheckoutScreen.Row>;
};

GiftCardsCheckoutScreen.TotalRow = () => {
  const { total } = useGiftCardsBasket();

  return <GiftCardsCheckoutScreen.Row amount={total}>Total</GiftCardsCheckoutScreen.Row>;
};

type RowProps = ComponentPropsWithoutRef<'div'> &
  PropsWithChildren & {
    amount: number;
  };

GiftCardsCheckoutScreen.Row = ({ className, amount, children, ...rest }: RowProps) => {
  const { formatCurrency } = useAppMethods();

  return (
    <div className={classnames('flex justify-between text-sm', className)} {...rest}>
      {children}
      <span>{formatCurrency(amount)}</span>
    </div>
  );
};
